import { useState } from "react";
import { CardElement, Elements, useElements, useStripe } from "@stripe/react-stripe-js";
import { Stripe } from "@stripe/stripe-js";

import { View, Text, Button } from "../base";
import { Dialog } from "../base/Dialog";
import { useApiRequest } from "../../hooks/useApiRequest";

type SavePaymentMethodDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  stripePromise: Promise<Stripe | null>;
};

const SaveCardForm = ({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const setupIntentRequest = useApiRequest<{ data: { clientSecret: string } }>();
  const confirmSetupRequest = useApiRequest<{ success: boolean }>();

  const handleSubmit = async () => {
    if (!stripe || !elements) return;

    setIsLoading(true);
    setError("");

    try {
      // Create SetupIntent on backend
      const setupResponse = await setupIntentRequest.fetch("/v1/payments/setup-intent", {
        method: "POST",
      });

      const clientSecret = setupResponse.data?.data?.clientSecret;
      if (!clientSecret) {
        setError("Failed to initialize card setup");
        setIsLoading(false);
        return;
      }

      // Confirm setup with Stripe
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        setError("Card element not found");
        setIsLoading(false);
        return;
      }

      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card: cardElement },
      });

      if (stripeError) {
        setError(stripeError.message || "Failed to save card");
        setIsLoading(false);
        return;
      }

      if (setupIntent?.payment_method) {
        // Save paymentMethodId on backend
        await confirmSetupRequest.fetch("/v1/payments/confirm-setup", {
          method: "POST",
          body: { paymentMethodId: setupIntent.payment_method },
        });

        onSaved();
        onClose();
      }
    } catch (err) {
      setError("An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <View className="p-4 space-y-4">
      <Text className="text-sm text-gray-600 dark:text-gray-400">Add a card to enable auto-pay. Your card information is securely processed by Stripe.</Text>
      <View className="border border-gray-200 dark:border-gray-600 rounded-lg p-3 bg-white dark:bg-gray-800">
        <CardElement
          options={{
            style: {
              base: {
                fontSize: "14px",
                color: "#374151",
                "::placeholder": { color: "#9CA3AF" },
              },
            },
          }}
        />
      </View>
      {error && <Text className="text-sm text-red-500 dark:text-red-400">{error}</Text>}
      <View className="flex flex-row gap-2 justify-end">
        <Button
          variant="neutral"
          onClick={onClose}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          variant="success"
          onClick={handleSubmit}
          disabled={isLoading || !stripe}
        >
          {isLoading ? "Saving..." : "Save Card"}
        </Button>
      </View>
    </View>
  );
};

export const SavePaymentMethodDialog = ({ isOpen, onClose, onSaved, stripePromise }: SavePaymentMethodDialogProps) => {
  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Add Payment Method"
      size="sm"
    >
      <Elements stripe={stripePromise}>
        <SaveCardForm
          onClose={onClose}
          onSaved={onSaved}
        />
      </Elements>
    </Dialog>
  );
};
